'use client';

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface AsyncSelectProps<T = any>
  extends Omit<React.ComponentProps<'input'>, 'onChange' | 'value'> {
  value?: T | null;
  onChange: (value: T | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  loadSuggestions: (query: string) => Promise<T[]>;
  debounceMs?: number;
  minChars?: number;
  maxSuggestions?: number;
  renderItem?: (
    item: T,
    index: number,
    isHighlighted: boolean,
    onSelect: (item: T) => void,
  ) => React.ReactNode;
  renderNoResults?: () => React.ReactNode;
  getDisplayValue?: (item: T) => string;
  getSearchValue?: (item: T) => string;
  error?: string | boolean;
  required?: boolean;
}

const AsyncSelect = <T,>(
  props: AsyncSelectProps<T> & { ref?: React.Ref<HTMLInputElement> },
) => {
  const {
    value = null,
    onChange,
    placeholder = 'Search...',
    className = '',
    disabled = false,
    loadSuggestions,
    debounceMs = 300,
    minChars = 1,
    maxSuggestions = 30,
    renderItem,
    renderNoResults,
    getDisplayValue = (item: T) => String(item),
    getSearchValue = (item: T) => String(item),
    error,
    required = false,
    ref,
    ...inputProps
  } = props;

  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(
    null,
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const displayValue = value ? getDisplayValue(value) : '';

  const debouncedLoad = useCallback(
    async (query: string) => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      const timer = setTimeout(async () => {
        if (query.length < minChars) {
          setSuggestions([]);
          setIsLoading(false);
          return;
        }

        setIsLoading(true);
        try {
          const results = await loadSuggestions(query);
          setSuggestions(results.slice(0, maxSuggestions));
        } catch (error) {
          console.error('Error loading suggestions:', error);
          setSuggestions([]);
        } finally {
          setIsLoading(false);
        }
      }, debounceMs);

      setDebounceTimer(timer);
    },
    [loadSuggestions, debounceMs, minChars, maxSuggestions, debounceTimer],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchValue = e.target.value;
      setSearchValue(newSearchValue);
      setHighlightedIndex(-1);

      // If user clears the input completely, clear the selection
      if (newSearchValue === '' && value) {
        onChange(null);
      }

      if (newSearchValue.trim()) {
        setShowSuggestions(true);
        debouncedLoad(newSearchValue);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    },
    [debouncedLoad, value, onChange],
  );

  const handleItemSelect = useCallback(
    (item: T) => {
      onChange(item);
      setSearchValue('');
      setShowSuggestions(false);
      setHighlightedIndex(-1);
      setSuggestions([]);
      inputRef.current?.blur();
    },
    [onChange],
  );

  const closeSuggestions = useCallback(() => {
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    // Don't reset search value here - let it persist
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showSuggestions || suggestions.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : prev,
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
            handleItemSelect(suggestions[highlightedIndex]);
          }
          break;
        case 'Escape':
          closeSuggestions();
          break;
      }
    },
    [
      showSuggestions,
      suggestions,
      highlightedIndex,
      handleItemSelect,
      closeSuggestions,
    ],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (suggestionsRef.current?.contains(e.relatedTarget as Node)) {
        return;
      }
      closeSuggestions();
    },
    [closeSuggestions],
  );

  const handleFocus = useCallback(() => {
    // Only set search value to selected value if search is currently empty
    if (value && !searchValue) {
      setSearchValue(getSearchValue(value));
    }

    // Show suggestions if we have a search value that meets minimum characters
    const currentSearchValue =
      searchValue || (value ? getSearchValue(value) : '');
    if (currentSearchValue && currentSearchValue.length >= minChars) {
      setShowSuggestions(true);
      if (suggestions.length === 0) {
        debouncedLoad(currentSearchValue);
      }
    }
  }, [
    value,
    searchValue,
    minChars,
    suggestions.length,
    debouncedLoad,
    getSearchValue,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        closeSuggestions();
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions, closeSuggestions]);

  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  const hasVisibleSuggestions =
    showSuggestions && (suggestions.length > 0 || isLoading);
  const inputValue = showSuggestions ? searchValue : displayValue;

  return (
    <div ref={containerRef} className="tw-relative tw-w-full">
      <Input
        {...inputProps}
        ref={ref || inputRef}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={cn(
          'tw-w-full',
          'tw-bg-white',
          'tw-placeholder:text-muted-foreground',
          className,
        )}
        disabled={disabled}
        autoComplete="off"
        error={error}
        required={required}
      />

      {hasVisibleSuggestions && (
        <div
          ref={suggestionsRef}
          className={cn(
            'tw-absolute tw-top-full tw-left-0 tw-right-0 tw-z-50 tw-mt-1 tw-bg-white tw-text-foreground tw-border tw-border-border tw-rounded-md tw-shadow-md tw-max-h-60 tw-overflow-y-auto',
          )}
        >
          {isLoading && (
            <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-muted-foreground tw-flex tw-items-center tw-gap-2">
              <div className="tw-w-4 tw-h-4 tw-border-2 tw-border-border tw-border-t-primary tw-rounded-full tw-animate-spin" />
              Loading...
            </div>
          )}

          {!isLoading &&
            suggestions.length === 0 &&
            (renderNoResults ? (
              renderNoResults()
            ) : (
              <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-muted-foreground">
                No results found
              </div>
            ))}

          {!isLoading &&
            suggestions.map((suggestion, index) => {
              const isHighlighted = index === highlightedIndex;

              if (renderItem) {
                return (
                  <div
                    key={index}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {renderItem(
                      suggestion,
                      index,
                      isHighlighted,
                      handleItemSelect,
                    )}
                  </div>
                );
              }

              return (
                <Button
                  key={index}
                  variant="ghost"
                  className={cn(
                    'tw-w-full !tw-justify-start !tw-text-left tw-h-auto tw-px-3 tw-py-2 tw-text-sm tw-font-normal tw-rounded-none tw-shadow-none',
                    isHighlighted && 'tw-bg-accent tw-text-accent-foreground',
                  )}
                  onClick={() => handleItemSelect(suggestion)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {getDisplayValue(suggestion)}
                </Button>
              );
            })}
        </div>
      )}
    </div>
  );
};

AsyncSelect.displayName = 'AsyncSelect';

export default AsyncSelect;
